import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useLocale, useLogger} from '#imports';
import {readonlyState} from '@malt/utils';
import type {FreelancerVertical, StrategyBackground} from '@api';
import {
    Configuration,
    type CurrencyCode,
    type DailyRate,
    type DailyRateStatResource,
    type Education,
    type ExperienceLevelResource,
    FreelancerSignupApi,
    FreelancerSignupFunctionalErrorCodeEnum,
    type FreelancerSignupWorkflow,
    type FunctionalSkillResource,
    type LanguageAndLevel,
    LanguageLevel,
    type LocationAndWorkplacePreference,
    type PostalAddress,
    type ProfilePicture,
    type WorkplacePreference,
} from '@api';
import type {AccountCompletionDraft, AccountCreationFormData, AccountLoginPasswordDraft} from '~/features/account/types/AccountCreationFormData';
import type {EditEducation} from '~/features/education/types/EditEducation';
import type {EditExperience} from '~/features/experience/types/EditExperience';
import type {SignupExperienceFormData} from '~/features/freelancer-signup/experience/types/SignupExperienceFormData';
import type {FreelancingExperienceId} from '~/features/freelancing-experience/types/FreelancingExperience';
import type {Skill} from '~/features/skills/types/Skill';
import {useFreelancerSignupFunctionalError} from '../composables/useFreelancerSignupFunctionalError';
import {toApi, toDomain} from '../helpers/WorkflowMapper';
import type {FreelancerSignupCategorization} from '../types/FreelancerSignupCategorization';
import type {Workflow, WorkflowCreationRequest, WorkflowRepatriationMetadata} from '../types/Workflow';

export const useFreelancerSignupStore = defineStore('freelancer-signup-store', () => {
    // Composables
    const logger = useLogger();
    const {isWorkflowFunctionalError} = useFreelancerSignupFunctionalError();
    const {countryCode, isMultiLocaleHost} = useLocale();

    // Refs
    const workflow = ref<Workflow | null>(null);

    const accountLoginPassword = ref<AccountCreationFormData | undefined>();
    const categorization = ref<string[]>([]);
    const categorizationOther = ref<string | undefined>();
    const categorizationSelectedByPrediction = ref<boolean>(false);
    const currencyCode = ref<CurrencyCode>();
    const dailyRate = ref<DailyRate>();
    const dailyRateAverageAndFees = ref<DailyRateStatResource>();
    const education = ref<Education[]>([]);
    const globalSkills = ref<string[]>([]);
    const hasImportedResume = ref<boolean>(false);
    const industryExpertiseHumanIds = ref<string[]>([]);
    const jobTitle = ref<string>('');
    const functionalSkillIds = ref<string[]>([]);
    const levelOfExperience = ref<ExperienceLevelResource>();
    const locationAndWorkplacePreference = ref<LocationAndWorkplacePreference>();
    const minPriceForCountry = ref<number | undefined>();
    const nativeLanguage = ref<LanguageAndLevel | undefined>();
    const otherLanguages = ref<LanguageAndLevel[]>([]);
    const phoneNumber = ref<string>();
    const picture = ref<ProfilePicture>();
    const privacyOptOut = ref<boolean>(false);
    const repatriationMetadata = ref<WorkflowRepatriationMetadata>();
    const strategyBackgrounds = ref<StrategyBackground[]>([]);
    const topSkills = ref<string[]>([]);
    const willingnessToTravelInternationally = ref<boolean | undefined>();
    const workExperiences = ref<SignupExperienceFormData[]>([]);

    // Computed values
    const preferredCategory = computed(() =>
        categorization.value && categorization.value.length > 0 ? categorization.value[0] : categorizationOther.value,
    );
    const isNewsletterOptInLogic = computed(() => isMultiLocaleHost.value || !['FR', 'AE'].includes(countryCode.value));

    // API
    const _apiConfiguration = new Configuration({basePath: ''});
    const _freelancerSignupApi = new FreelancerSignupApi(_apiConfiguration);

    // Functions

    async function addEducation(education: EditEducation): Promise<void> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.addEducation({
            educationContentRequest: toApi.educationContentRequest(education),
        });
        _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function addNewExperience(experience: EditExperience): Promise<void> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.addWorkExperience({
            workExperienceContentRequest: toApi.workExperienceContentRequest(experience, false),
        });
        _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function askNewEmailValidationCode(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.askNewEmailValidationCode();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function askWorkflowOwnershipValidationCode(email: string): Promise<void> {
        await _freelancerSignupApi.askWorkflowOwnershipValidationCode({
            workflowOwnershipValidationCodeRequest: {
                email,
            },
        });
    }

    async function deleteAccount(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.deleteAccountDraft();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function finishWorkflow(): Promise<void> {
        await _freelancerSignupApi.finishWorkflow({finishWorkflowRequest: {}});
    }

    async function getDailyRateAverageAndFees(): Promise<DailyRateStatResource | undefined> {
        if (!levelOfExperience.value) {
            return;
        }
        try {
            dailyRateAverageAndFees.value = await _freelancerSignupApi.getAverageDailyRateAndFees({
                dailyRateStatRequest: {
                    category: categorization.value.length > 0 ? categorization.value[0] : undefined,
                    experienceLevel: levelOfExperience.value!,
                },
            });
            return dailyRateAverageAndFees.value;
        } catch (e: any) {
            logger.error('freelancer-signup-store - getDailyRateAverageAndFees:', e);
        }
    }

    async function getMaybeWorkflow(): Promise<void> {
        try {
            const freelancerSignupWorkflow = await _freelancerSignupApi.findWorkflow();
            _updateWorkflowData(freelancerSignupWorkflow);
        } catch (e: any) {
            // Only ignore if the workflow doesn't exist
            if (isWorkflowFunctionalError(e, FreelancerSignupFunctionalErrorCodeEnum.NoWorkflowFound)) {
                workflow.value = null;
            } else {
                throw e;
            }
        }
    }

    async function getMaybeWorkflowByReminder(token: string): Promise<void> {
        try {
            const freelancerSignupWorkflow = await _freelancerSignupApi.associateWorkflowByReminderToUser({
                reminderUuid: token,
            });

            _updateWorkflowData(freelancerSignupWorkflow);
        } catch (e: any) {
            // Only ignore if the workflow doesn't exist
            if (isWorkflowFunctionalError(e, FreelancerSignupFunctionalErrorCodeEnum.NoWorkflowFound)) {
                workflow.value = null;
            } else {
                throw e;
            }
        }
    }

    function getMaybeWorkflowEmail(): string | undefined {
        return accountLoginPassword.value?.email || repatriationMetadata.value?.freelancerEmail;
    }

    async function initRepatriationMetadata(repatriationTokenId: string): Promise<void> {
        try {
            const getRepatriationMetadataRequest = toApi.initGetRepatriationMetadataRequest(repatriationTokenId);
            const metadata = await _freelancerSignupApi.getRepatriationMetadata(getRepatriationMetadataRequest);
            repatriationMetadata.value = toDomain.repatriationMetadata(metadata);
        } catch (error: any) {
            if (error?.statusCode === 404) {
                logger.info('freelancer-signup-store - get repatriation metadata:', error);
            } else {
                logger.error('freelancer-signup-store - get repatriation metadata:', error);
            }
        }
    }

    async function importResume(resume: File): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.importResume({resume});
        hasImportedResume.value = true;
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    function isFreelancerEmailDefined(): boolean {
        return !!accountLoginPassword.value?.email || !!repatriationMetadata.value?.freelancerEmail;
    }

    async function skipResumeImport(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.skipResumeImport();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function initWorkflowIfNeeded(request: WorkflowCreationRequest): Promise<Workflow> {
        if (workflow.value) {
            return workflow.value;
        }

        const initWorkflowRequest = toApi.initWorkflowOperationRequest(request);
        const freelancerSignupWorkflow = await _freelancerSignupApi.initWorkflow(initWorkflowRequest);
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function removeEducation(educationId: string): Promise<void> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.deleteEducation({educationId});
        _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function removeExperience(experienceId: string): Promise<void> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.deleteWorkExperience({workExperienceId: experienceId});
        _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function removeProfilePicture(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.deleteProfilePicture();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveStrategyBackground(strategyBackground: StrategyBackground[]): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateStrategyBackground({
            strategyBackground,
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveAccount(account: AccountLoginPasswordDraft): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.createAccountDraft({
            createAccountDraftRequest: {
                firstName: account.firstname,
                lastName: account.lastname,
                email: account.email,
                password: account.password,
                areTermsAndConditionsAccepted: account.isCheckedMaltsTermsAndConditions && account.isCheckedMaltsPrivacyPolicy,
                isOptedInToMarketingEmails: !isNewsletterOptInLogic.value,
            },
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function verifyEmail(email: string): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.verifyEmail({
            verifyEmailRequest: {email},
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveAccountCompletion(accountCompletion: AccountCompletionDraft): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.completeAccountDraft({
            completeAccountDraftRequest: {
                firstName: accountCompletion.firstname,
                lastName: accountCompletion.lastname,
                areTermsAndConditionsAccepted: accountCompletion.isCheckedMaltsTermsAndConditions,
                isOptedInToMarketingEmails: !isNewsletterOptInLogic.value,
            },
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveCategorization(categorization: FreelancerSignupCategorization): Promise<Workflow> {
        const request = toApi.updateCategoriesOperationRequest(categorization);
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateCategories(request);
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveCategorizationOther(categorizationOther: string): Promise<Workflow> {
        const request = toApi.updateOtherCategoryOperationRequest(categorizationOther);
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateOtherCategory(request);
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveDailyRate(rate: number, hidden: boolean): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateDailyRate({
            updateDailyRateRequest: {price: rate, hidden},
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveFreelancingExperience(request: {freelancingExperience: FreelancingExperienceId}): Promise<Workflow> {
        const freelancingExperienceRequest = toApi.freelancingExperienceRequest(request);
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateFreelancingExperience(freelancingExperienceRequest);
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveFunctionalAndGlobalSkills(
        selectedFunctionalSkills: FunctionalSkillResource[],
        selectedGlobalSkills: Skill[],
    ): Promise<Workflow> {
        const request = toApi.updateStrategySkillsRequest(selectedFunctionalSkills, selectedGlobalSkills);
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateStrategySkills({
            updateStrategySkillsRequest: request,
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveIndustryExpertises(industryExpertises: string[]): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateSignupIndustryExpertises({
            updateIndustryExpertisesRequest: {
                expertises: industryExpertises,
            },
        });

        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveJobTitle(newJobTitle: string): Promise<Workflow> {
        const freelancerSignupWorkflowUpdated = await _freelancerSignupApi.updateHeadline({updateHeadlineRequest: {headline: newJobTitle}});
        return _updateWorkflowData(freelancerSignupWorkflowUpdated);
    }

    async function saveLevelOfExperience(level: ExperienceLevelResource): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateExperienceLevel({
            updateExperienceLevelRequest: {experienceLevel: level},
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveLocationAndWorkplacePreferences(locationAndWorkplacePreference: {
        location: PostalAddress;
        workplacePreference: WorkplacePreference;
        willingnessToTravelInternationally: boolean | undefined;
    }): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateLocationAndWorkplace({
            updateLocationAndWorkplaceRequest: locationAndWorkplacePreference,
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function savePhoneNumber(phone: string): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updatePhoneNumber({
            updatePhoneNumberRequest: {phone},
        });

        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function savePrivacySettings(privacyOptOut: boolean): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updatePrivacy({
            updatePrivacyRequest: {
                privacyOptOut,
            },
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function saveTopSkills(topSkills: string[]): Promise<Workflow> {
        const freelancerSignupWorkflowUpdated = await _freelancerSignupApi.updateRegularSkills({
            updateRegularSkillsRequest: {globalTopSkills: topSkills},
        });
        return _updateWorkflowData(freelancerSignupWorkflowUpdated);
    }

    async function skipTopSkills(): Promise<Workflow> {
        const freelancerSignupWorkflowUpdated = await _freelancerSignupApi.skipRegularSkills();
        return _updateWorkflowData(freelancerSignupWorkflowUpdated);
    }

    async function saveWorkingLanguages(languages: {code: string; level: LanguageLevel}[]): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateLanguages({
            updateLanguagesRequest: {selectedLanguages: languages},
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function skipPhoneNumber(): Promise<Workflow> {
        const freelancerSignupWorkflowUpdated = await _freelancerSignupApi.skipPhoneNumber();
        return _updateWorkflowData(freelancerSignupWorkflowUpdated);
    }

    async function skipProfilePicture(): Promise<Workflow> {
        const freelancerSignupWorkflowUpdated = await _freelancerSignupApi.skipProfilePicture();
        return _updateWorkflowData(freelancerSignupWorkflowUpdated);
    }

    async function updateEducation(education: EditEducation): Promise<void> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateEducation({
            educationId: education.id!,
            educationContentRequest: toApi.educationContentRequest(education),
        });
        _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function updateExperience(experience: EditExperience): Promise<void> {
        const imported = workExperiences.value.find((xp) => xp.id === experience.id)?.imported || false;
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateWorkExperience({
            workExperienceId: experience.id!,
            workExperienceContentRequest: toApi.workExperienceContentRequest(experience, imported),
        });
        _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function uploadProfilePicture(blob: Blob): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateProfilePicture({
            profilePicture: blob,
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function updateWorkflowContext(vertical: FreelancerVertical) {
        const freelancerSignupWorkflow = await _freelancerSignupApi.updateWorkflowContext({
            updateWorkflowContextRequest: {newVertical: vertical},
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function validateEducation(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.validateEducations();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function validateExperiences(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.validateWorkExperiences();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function skipWorkExperiences(): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.skipWorkExperiences();
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function validateWorkflowOwnership(email: string, code: string): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.validateWorkflowOwnership({
            validateWorkflowOwnershipRequest: {
                email,
                validationCode: code,
            },
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    async function verifyEmailValidationCode(code: string): Promise<Workflow> {
        const freelancerSignupWorkflow = await _freelancerSignupApi.validateEmailValidationCode({
            validateEmailValidationCodeRequest: {validationCode: code},
        });
        return _updateWorkflowData(freelancerSignupWorkflow);
    }

    return {
        // RO A-Z
        account: readonlyState(accountLoginPassword),
        categorization: readonlyState(categorization),
        categorizationOther: readonlyState(categorizationOther),
        categorizationSelectedByPrediction: readonlyState(categorizationSelectedByPrediction),
        currencyCode: readonlyState(currencyCode),
        dailyRate: readonlyState(dailyRate),
        dailyRateAverageAndFees: readonlyState(dailyRateAverageAndFees),
        education: readonlyState(education),
        functionalSkillIds: readonlyState(functionalSkillIds),
        globalSkills: readonlyState(globalSkills),
        importedResume: readonlyState(hasImportedResume),
        industryExpertiseHumanIds: readonlyState(industryExpertiseHumanIds),
        isNewsletterOptInLogic,
        jobTitle: readonlyState(jobTitle),
        levelOfExperience: readonlyState(levelOfExperience),
        locationAndWorkplacePreference: readonlyState(locationAndWorkplacePreference),
        minPriceForCountry: readonlyState(minPriceForCountry),
        nativeLanguage: readonlyState(nativeLanguage),
        otherLanguages: readonlyState(otherLanguages),
        phoneNumber: readonlyState(phoneNumber),
        picture: readonlyState(picture),
        preferredCategory,
        privacyOptOut: readonlyState(privacyOptOut),
        repatriationMetadata: readonlyState(repatriationMetadata),
        strategyBackgrounds: readonlyState(strategyBackgrounds),
        topSkills: readonlyState(topSkills),
        willingnessToTravelInternationally: readonlyState(willingnessToTravelInternationally),
        workExperiences: readonlyState(workExperiences),
        workflow: readonlyState(workflow),

        // functions A-Z
        addEducation,
        addNewExperience,
        askNewEmailValidationCode,
        askWorkflowOwnershipValidationCode,
        deleteAccount,
        finishWorkflow,
        getDailyRateAverageAndFees,
        getMaybeWorkflow,
        getMaybeWorkflowByReminder,
        getMaybeWorkflowEmail,
        importResume,
        initRepatriationMetadata,
        initWorkflowIfNeeded,
        isFreelancerEmailDefined,
        skipResumeImport,
        removeEducation,
        removeExperience,
        removeProfilePicture,
        saveStrategyBackground,
        saveAccount,
        saveAccountCompletion,
        saveCategorization,
        saveCategorizationOther,
        saveDailyRate,
        saveFreelancingExperience,
        saveFunctionalAndGlobalSkills,
        saveIndustryExpertises,
        saveJobTitle,
        saveLevelOfExperience,
        saveLocationAndWorkplacePreferences,
        savePhoneNumber,
        savePrivacySettings,
        saveTopSkills,
        saveWorkingLanguages,
        skipPhoneNumber,
        skipProfilePicture,
        skipTopSkills,
        skipWorkExperiences,
        updateEducation,
        updateExperience,
        uploadProfilePicture,
        updateWorkflowContext,
        validateEducation,
        validateExperiences,
        validateWorkflowOwnership,
        verifyEmail,
        verifyEmailValidationCode,
    };

    function _updateWorkflowData(freelancerSignupWorkflow: FreelancerSignupWorkflow): Workflow {
        workflow.value = toDomain.workflow(freelancerSignupWorkflow);

        accountLoginPassword.value = toDomain.account(freelancerSignupWorkflow.freelancerSignupAccountDraft);
        categorization.value = toDomain.categorization(freelancerSignupWorkflow.freelancerSignupProfileDraft.categories);
        categorizationOther.value = toDomain.categorizationOther(freelancerSignupWorkflow.freelancerSignupProfileDraft.otherCategory);
        categorizationSelectedByPrediction.value = !!freelancerSignupWorkflow.freelancerSignupProfileDraft.categories?.selectedByPredictions;
        currencyCode.value = freelancerSignupWorkflow.context.currencyCode;
        dailyRate.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.dailyRate;
        education.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.educations;
        functionalSkillIds.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.functionalSkillIds || [];
        globalSkills.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.globalSkills || [];
        industryExpertiseHumanIds.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.industryExpertiseIds || [];
        jobTitle.value = toDomain.jobTitle(freelancerSignupWorkflow.freelancerSignupProfileDraft.headline);
        levelOfExperience.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.experienceLevel;
        locationAndWorkplacePreference.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.locationAndWorkplacePreference;
        minPriceForCountry.value = freelancerSignupWorkflow.context.minPriceForCountry;
        nativeLanguage.value = _findNativeLanguage(freelancerSignupWorkflow.freelancerSignupProfileDraft.languages);
        otherLanguages.value = _findOtherLanguages(nativeLanguage.value, freelancerSignupWorkflow.freelancerSignupProfileDraft.languages);
        phoneNumber.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.phoneNumber;
        picture.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.profilePicture;
        privacyOptOut.value = freelancerSignupWorkflow.freelancerSignupProfileDraft?.privacyOptOut || false;
        repatriationMetadata.value = toDomain.repatriationMetadata(freelancerSignupWorkflow.context.repatriationMetadata);
        topSkills.value = toDomain.topSkills(freelancerSignupWorkflow.freelancerSignupProfileDraft.globalTopSkills);
        strategyBackgrounds.value = toDomain.strategyBackgrounds(freelancerSignupWorkflow.freelancerSignupProfileDraft.strategyBackgrounds);
        willingnessToTravelInternationally.value = freelancerSignupWorkflow.freelancerSignupProfileDraft.willingnessToTravelInternationally;
        workExperiences.value = toDomain.experiences(freelancerSignupWorkflow.freelancerSignupProfileDraft.workExperiences);

        return workflow.value;
    }

    function _findNativeLanguage(languages: Array<LanguageAndLevel>): LanguageAndLevel | undefined {
        const maybeNative = languages.find((lang) => lang.languageLevel === LanguageLevel.Native);
        if (!maybeNative) {
            return languages.length > 0 ? languages[0] : undefined;
        }
        return maybeNative;
    }

    function _findOtherLanguages(native: LanguageAndLevel | undefined, languages: Array<LanguageAndLevel>): LanguageAndLevel[] {
        if (!native) {
            return [];
        }
        return languages.filter((lang) => lang.languageCode !== native.languageCode);
    }
});
